import { Paper, Stack, Text, Group, Progress, Checkbox, ThemeIcon, Button, Collapse, ActionIcon } from '@mantine/core';
import { IconRocket, IconX, IconBox, IconMapPin, IconUsers, IconQrcode, IconReportAnalytics, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';

interface OnboardingChecklistProps {
  assetCount: number;
  locationCount: number;
  peopleCount: number;
  userCount?: number;
}

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action: () => void;
  actionLabel: string;
}

const DISMISSED_KEY = 'trustpuma_onboarding_dismissed';
const COLLAPSED_KEY = 'trustpuma_onboarding_collapsed';
const REPORT_VIEWED_KEY = 'trustpuma_onboarding_report_viewed';

export function OnboardingChecklist({ assetCount, locationCount, peopleCount, userCount = 1 }: OnboardingChecklistProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [reportViewed, setReportViewed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const isDismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
    const isCollapsed = localStorage.getItem(COLLAPSED_KEY) === 'true';
    const hasViewedReport = localStorage.getItem(REPORT_VIEWED_KEY) === 'true';
    setDismissed(isDismissed);
    setCollapsed(isCollapsed);
    setReportViewed(hasViewedReport);
  }, []);

  const checklist: ChecklistItem[] = useMemo(() => [
    {
      id: 'asset',
      label: 'Add your first asset',
      description: 'Create an asset to start tracking your equipment',
      icon: <IconBox size={18} />,
      completed: assetCount > 0,
      action: () => navigate('/assets'),
      actionLabel: 'Go to Assets',
    },
    {
      id: 'location',
      label: 'Create a location',
      description: 'Organize assets by where they are',
      icon: <IconMapPin size={18} />,
      completed: locationCount > 0,
      action: () => navigate('/locations'),
      actionLabel: 'Go to Locations',
    },
    {
      id: 'team',
      label: 'Invite a team member',
      description: 'Collaborate with your team',
      icon: <IconUsers size={18} />,
      completed: userCount > 1,
      action: () => navigate('/settings/users'),
      actionLabel: 'Invite User',
    },
    {
      id: 'qrcode',
      label: 'Generate a QR code',
      description: 'Print labels for fast mobile scanning',
      icon: <IconQrcode size={18} />,
      completed: assetCount > 0, // Can generate QR if has assets
      action: () => navigate('/assets'),
      actionLabel: 'View Assets',
    },
    {
      id: 'report',
      label: 'Run your first report',
      description: 'Get insights into your asset inventory',
      icon: <IconReportAnalytics size={18} />,
      completed: reportViewed,
      action: () => navigate('/reports'),
      actionLabel: 'View Reports',
    },
  ], [assetCount, locationCount, userCount, reportViewed, navigate]);

  const completedCount = checklist.filter(item => item.completed).length;
  const progress = (completedCount / checklist.length) * 100;
  const allComplete = completedCount === checklist.length;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    localStorage.setItem(COLLAPSED_KEY, String(newCollapsed));
    setCollapsed(newCollapsed);
  };

  // Don't show if dismissed or all complete
  if (dismissed || allComplete) {
    return null;
  }

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      style={{
        background: 'linear-gradient(135deg, rgba(11, 72, 124, 0.05) 0%, rgba(26, 110, 181, 0.05) 100%)',
        borderColor: 'var(--mantine-color-blue-3)',
      }}
    >
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ThemeIcon size={40} radius="md" variant="gradient" gradient={{ from: '#0b487c', to: '#1a6eb5', deg: 135 }}>
              <IconRocket size={22} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">Getting Started</Text>
              <Text size="sm" c="dimmed">
                {completedCount} of {checklist.length} tasks complete
              </Text>
            </div>
          </Group>
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleToggleCollapse}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <IconChevronDown size={18} /> : <IconChevronUp size={18} />}
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleDismiss}
              title="Dismiss"
            >
              <IconX size={18} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Progress Bar */}
        <Progress
          value={progress}
          size="sm"
          radius="xl"
          color="blue"
        />

        {/* Checklist Items */}
        <Collapse in={!collapsed}>
          <Stack gap="xs" mt="xs">
            {checklist.map((item) => (
              <Paper
                key={item.id}
                p="sm"
                radius="sm"
                style={{
                  backgroundColor: item.completed
                    ? 'var(--mantine-color-green-0)'
                    : 'var(--mantine-color-body)',
                  opacity: item.completed ? 0.8 : 1,
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                    <Checkbox
                      checked={item.completed}
                      readOnly
                      size="md"
                      color="green"
                      styles={{
                        input: { cursor: 'default' },
                      }}
                    />
                    <ThemeIcon
                      size={32}
                      radius="sm"
                      variant="light"
                      color={item.completed ? 'green' : 'blue'}
                    >
                      {item.icon}
                    </ThemeIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="sm"
                        fw={500}
                        td={item.completed ? 'line-through' : undefined}
                        c={item.completed ? 'dimmed' : undefined}
                      >
                        {item.label}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {item.description}
                      </Text>
                    </div>
                  </Group>
                  {!item.completed && (
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      onClick={item.action}
                    >
                      {item.actionLabel}
                    </Button>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        </Collapse>

        {/* Collapsed summary */}
        {collapsed && (
          <Text size="sm" c="dimmed" ta="center">
            Click to expand and continue setting up your account
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
